import { useState } from 'react'
import useApi from '../common/utils/api/useApi'
import {
  IhiGender,
  PatientClient,
  PatientIhiSearchResponse,
  PatientIhiSearchSuccessfulResponse,
} from '../features/patient/PatientContextSlice'
import {
  ClinicClient,
  HpioValidateResponse,
} from '../pages/NewScriptPage/ScriptForm/ClinicContextSlice'
import {
  HpiiValidateResponse,
  PrescriberClient,
} from '../pages/NewScriptPage/ScriptForm/PrescriberSlice'
import { useAppSelector } from '../app/typedReduxHooks'
import { TenantClient } from '../features/tenant/tenantSlice'
import { StepValidationStatus } from '../types/hi-validation'

const hpiiResponseValidator = (response: HpiiValidateResponse | undefined): boolean =>
  response ? response.hpii_status === 'A' : false

const hpioResponseValidator = (response: HpioValidateResponse | undefined): boolean =>
  response ? response.hpio_status === 'A' : false

const ihiResponseValidator = (response: PatientIhiSearchResponse | undefined): boolean => {
  if (!response) return false
  // Type guard: check if response is successful response type
  if ('HIStatus' in response && 'RecordStatus' in response) {
    const successResponse = response as PatientIhiSearchSuccessfulResponse
    return successResponse.HIStatus === 'Active' && successResponse.RecordStatus === 'Verified'
  }
  return false
}

const useHIValidate = () => {
  const [hpiiValid, setHpiiValid] = useState<StepValidationStatus>('NOT_START')
  const [hpioValid, setHpioValid] = useState<StepValidationStatus>('NOT_START')
  const [ihiValid, setIhiValid] = useState<StepValidationStatus>('NOT_START')
  const prescriber = useAppSelector((state) => state.prescriber.prescriber)
  const selectedPatient = useAppSelector((state) => state.patientDetail.selectedPatient)
  const clinic = useAppSelector((state) => state.clinic.currentClinic)
  const organizationSettings = useAppSelector((state) => state.organization?.data?.settings)
  const enableWebhooks = !!organizationSettings?.enableWebhooks

  const {
    trigger: checkHpii,
    loading: hpiiLoading,
    error: hpiiError,
    reset: resetCheckHpiiRequest,
  } = useApi(PrescriberClient.checkHpii, {
    onSuccess: (response) => setHpiiValid(hpiiResponseValidator(response) ? 'VALID' : 'INVALID'),
  })

  const {
    trigger: checkHpio,
    loading: hpioLoading,
    error: hpioError,
    reset: resetCheckHpioRequest,
  } = useApi(ClinicClient.checkHpio, {
    onSuccess: (response) => setHpioValid(hpioResponseValidator(response) ? 'VALID' : 'INVALID'),
  })

  const {
    trigger: checkIhi,
    loading: ihiLoading,
    error: ihiError,
    reset: resetCheckIhiRequest,
  } = useApi(PatientClient.checkIhi, {
    onSuccess: (response) => setIhiValid(ihiResponseValidator(response) ? 'VALID' : 'INVALID'),
  })

  const { trigger: publishHICheckErrors } = useApi(TenantClient.publishHICheckErrors)

  const validateHI = async () => {
    try {
      // Run all three validations in parallel for better performance
      const [hpiiResponse, hpioResponse, ihiResponse] = await Promise.all([
        checkHpii({
          hpii: prescriber?.hpii ?? '',
          family_name: prescriber?.familyName ?? '',
          clinicId: clinic?.id ?? '',
        }),
        checkHpio({ clinic_id: clinic?.id, hpii_number: prescriber?.hpii }),
        checkIhi({
          GivenName: selectedPatient?.given_names,
          FamilyName: selectedPatient?.family_name,
          DOB: selectedPatient?.date_of_birth,
          Gender: selectedPatient?.gender as IhiGender,
          IhiNumber: selectedPatient?.ihi_number,
          HpiiNumber: prescriber?.hpii,
          ClinicId: clinic?.id,
        }),
      ])

      const hpiiResult = hpiiResponseValidator(hpiiResponse)
      const hpioResult = hpioResponseValidator(hpioResponse)
      const ihiResult = ihiResponseValidator(ihiResponse)

      const isHIValid = hpiiResult && hpioResult && ihiResult

      // Publish errors if validation failed and webhooks are enabled
      if (!isHIValid && enableWebhooks && selectedPatient?.id) {
        // Fire-and-forget: don't await to avoid blocking the return
        publishHICheckErrors({
          patientId: selectedPatient.id,
          isHPIIValid: hpiiResult,
          isHPIOValid: hpioResult,
          isIHIValid: ihiResult,
        }).catch((error) => {
          // Silently log webhook errors to avoid breaking the validation flow
          console.error('Failed to publish HI check errors:', error)
        })
      }

      return {
        hpii: hpiiResult,
        hpio: hpioResult,
        ihi: ihiResult,
      }
    } catch (error) {
      // If any validation fails, set all to false and re-throw
      console.error('HI validation failed:', error)
      throw error
    }
  }
  return {
    validateHI,
    hpiiValidation: {
      isLoading: hpiiLoading,
      error: hpiiError,
      reset: resetCheckHpiiRequest,
      status: hpiiValid,
    },
    hpioValidation: {
      isLoading: hpioLoading,
      error: hpioError,
      reset: resetCheckHpioRequest,
      status: hpioValid,
    },
    ihiValidation: {
      isLoading: ihiLoading,
      error: ihiError,
      reset: resetCheckIhiRequest,
      status: ihiValid,
    },
  }
}

export default useHIValidate
